// Authentication types

export enum UserRole {
  USER = 'USER',
  ADMIN = 'ADMIN'
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  role: UserRole;
  active: boolean;
  authSource?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  role: UserRole;
}

export interface UpdateUserRequest {
  firstName: string;
  lastName: string;
  username: string;
  password?: string;
  role: UserRole;
  active: boolean;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

// ─── Energy Portal Types ────────────────────────────────────────────────────

export type MeterType = 'STROM' | 'GAS' | 'WASSER' | 'PV';

export type BuildingStatus =
  | 'NEU'
  | 'ANGEBOT_ERHALTEN'
  | 'BEZAHLT'
  | 'INSTALLATION_TERMINIERT'
  | 'IN_BETRIEB'
  | 'STOERUNG';

export type TimeFilter =
  | 'TODAY'
  | 'THIS_WEEK'
  | 'THIS_MONTH'
  | 'LAST_MONTH'
  | 'THIS_YEAR'
  | 'LAST_YEAR';

export interface Building {
  id: number;
  name: string;
  address?: string;
  hasPv: boolean;
  active: boolean;
  meterCount: number;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  status?: BuildingStatus;
  statusChangedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBuildingRequest {
  name: string;
  address?: string;
  hasPv: boolean;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  status?: BuildingStatus;
  statusChangedAt?: string;
}

export interface UpdateBuildingRequest {
  name?: string;
  address?: string;
  hasPv?: boolean;
  active?: boolean;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  status?: BuildingStatus;
  statusChangedAt?: string;
}

export interface Meter {
  id: number;
  buildingId: number;
  type: MeterType;
  name?: string;
  unit: string;
  externalId?: string;
  meterNumber?: string;
  consumptionPointNumber?: string;
  energySupplier?: string;
  currentReading?: number;
  active: boolean;
}

export interface CreateMeterRequest {
  type: MeterType;
  name?: string;
  unit: string;
  meterNumber?: string;
  consumptionPointNumber?: string;
  energySupplier?: string;
}

export interface UpdateMeterRequest {
  name?: string;
  meterNumber?: string;
  consumptionPointNumber?: string;
  energySupplier?: string;
  currentReading?: number | null;
}

export interface ReadingDataPoint {
  period: string;
  value: number;
}

export interface EnergyReadingsResponse {
  meterId: number;
  meterType: MeterType;
  unit: string;
  data: ReadingDataPoint[];
}

export interface PvDataPoint {
  period: string;
  productionKwh: number;
  feedInKwh: number;
  selfConsumptionKwh: number;
}

export interface PvReadingsResponse {
  meterId: number;
  data: PvDataPoint[];
  /**
   * Ab wann eine Einspeisungsmessung vorliegt (ISO), `null` wenn gar keine.
   * Davor ist der Eigenverbrauch nicht bekannt — siehe Dashboard.
   */
  feedInSince: string | null;
}

export interface MeterLogEntry {
  timestamp: string;
  value: number;
}

export interface MeterLogResponse {
  meterId: number;
  meterType: string;
  unit: string;
  connectedSince?: string;
  totalEntries: number;
  entries: MeterLogEntry[];
}

export interface DeleteReadingsResponse {
  meterId: number;
  deletedReadings: number;
  currentReadingCleared: boolean;
  baselinesReset: number;
  message: string;
}

// ─── Building Notes ──────────────────────────────────────────────────────────

export interface BuildingNote {
  id: number;
  buildingId: number;
  userId: number;
  authorName: string;
  text: string;
  createdAt: string;
}

export interface CreateNoteRequest {
  text: string;
}

export interface UpdateNoteRequest {
  text: string;
}

// ─── Building Documents ──────────────────────────────────────────────────────

export interface BuildingDocument {
  id: number;
  buildingId: number;
  name: string;
  category?: string;
  uploadedBy: number;
  uploaderName?: string;
  createdAt?: string;
  fileName?: string;
}

// ─── Activity Feed ───────────────────────────────────────────────────────────

export interface ActivityFeedItem {
  id: number;
  type: 'NOTE' | 'STATUS_CHANGE';
  buildingId: number;
  buildingName: string;
  authorName: string;
  text?: string;
  oldStatus?: string;
  newStatus?: string;
  createdAt: string;
}

// ─── MQTT Ingest ────────────────────────────────────────────────────────────

export type MqttMessageStatus =
  | 'OK'
  | 'BASELINE'
  | 'UNMAPPED'
  | 'UNKNOWN_SCHEMA'
  | 'INVALID'
  | 'COUNTER_RESET'
  | 'IMPLAUSIBLE'
  | 'TELEMETRY'
  | 'ERROR';

export interface MqttStatus {
  enabled: boolean;
  connected: boolean;
  brokerUrl: string;
  clientId: string;
  subscribedTopics: string[];
  lastError: string | null;
  lastConnectedAt: string | null;
  deviceCount: number;
  onlineDeviceCount: number;
  unmappedChannelCount: number;
  messagesReceived: number;
  readingsStored: number;
  messagesFailed: number;
}

export interface MqttChannel {
  id: number;
  channelKey: string;
  meterId: number | null;
  meterName: string | null;
  meterType: MeterType | null;
  buildingName: string | null;
  /** PV-Zähler, der die Einspeisung dieses Kanals bekommt (nur bei Netzzählern sinnvoll). */
  feedInMeterId: number | null;
  feedInMeterName: string | null;
  lastTotal: number | null;
  lastValue: number | null;
  lastUnit: string | null;
  lastTotalAt: string | null;
  readingCount: number;
}

export interface MqttDevice {
  id: number;
  deviceId: string;
  name: string | null;
  /** `device` = die Platine, `meter` = der Zähler, den sie liest. */
  kind: string;
  deviceUid: string | null;
  /** Standort laut Gerät, roh. Kann eine Gebäude-id sein, muss aber nicht. */
  siteId: string | null;
  /** Das Gebäude zu `siteId`, sofern es sich auflösen ließ. */
  siteBuildingId: number | null;
  siteBuildingName: string | null;
  online: boolean;
  lastSeenAt: string | null;
  lastMessageAt: string | null;
  fwVersion: string | null;
  ipAddress: string | null;
  macAddress: string | null;
  hostname: string | null;
  wifiRssi: number | null;
  uptimeSeconds: number | null;
  freeMemory: number | null;
  cpuTemp: number | null;
  intervalSeconds: number | null;
  /** Bautyp des laufenden Images (PlatformIO-Env). Bestimmt, welches Binary passt. */
  fwTarget: string | null;
  /** Erste 8 Bytes des ELF-Hashes — daran entscheidet sich, ob ein Gerät aktuell ist. */
  elfSha256: string | null;
  messageCount: number;
  errorCount: number;
  channels: MqttChannel[];
}

export interface MqttMessageLogEntry {
  id: number;
  deviceId: string | null;
  topic: string;
  payload: string | null;
  status: MqttMessageStatus;
  detail: string | null;
  receivedAt: string;
}

export interface MqttDeviceConfigRequest {
  intervalSeconds?: number;
  extra?: Record<string, unknown>;
}

/**
 * Kopfdaten eines gespeicherten Zählerfotos.
 *
 * Ohne die Bytes: die Liste zeigt Zeitpunkte, das Bild selbst wird einzeln
 * geladen (und braucht dafür den Auth-Header, also einen Blob-Abruf statt
 * eines <img src>).
 */
export interface MqttMeterImageInfo {
  id: number;
  deviceRowId: number;
  sizeBytes: number;
  contentType: string;
  /** Aufnahmezeit laut Gerät; null, solange die Metadaten fehlen. */
  capturedAt: string | null;
  receivedAt: string;
  reason: string | null;
  deviceMac: string | null;
  /** false = die Metadaten fehlen noch, die Übertragung kann abgebrochen sein. */
  complete: boolean;
}

export interface MqttMeterImageList {
  meterId: number | null;
  /** false = zu diesem Zähler ist keine lesende Platine bekannt. */
  readerKnown: boolean;
  images: MqttMeterImageInfo[];
}
